import { useEffect, useCallback } from 'react';
import { useUser } from 'react-ui';
import { useCreatorModalSafe } from '../contexts/CreatorModal';

const SCHEME = 'balloon-events:';

/** All recognised deep-link actions */
type DeepLinkAction = { type: 'balloons/new' };

/**
 * Parse a `balloon-events://` URL into a typed action.
 * Returns null for unknown or malformed URLs.
 */
function parseDeepLink(url: string): DeepLinkAction | null {
    if (!url.startsWith(SCHEME)) return null;
    try {
        const parsed = new URL(url);
        // hostname + pathname → e.g. "balloons" + "/new" → "balloons/new"
        const path = `${parsed.hostname}${parsed.pathname}`.replace(/^\/+|\/+$/g, '');
        if (path === 'balloons/new') return { type: 'balloons/new' };
        return null;
    } catch {
        return null;
    }
}

/**
 * Intercepts `balloon-events://` deep links from two sources:
 *
 * 1. **Anchor clicks** — any `<a href="balloon-events://...">` anywhere on the
 *    page (e.g. zeppelin CTAs). The default navigation is suppressed and the
 *    action is handled inside the map.
 *
 * 2. **Custom window event** `balloonevents:deeplink` with `detail: string`
 *    (the full URL). Use this for programmatic triggers or to bridge native
 *    Capacitor deep links:
 *    ```ts
 *    window.dispatchEvent(
 *      new CustomEvent('balloonevents:deeplink', { detail: url })
 *    );
 *    ```
 */
export function useDeepLinkHandler(): void {
    const creatorModal = useCreatorModalSafe();
    const { requiresUser } = useUser();

    const handleUrl = useCallback(
        (url: string) => {
            const action = parseDeepLink(url);
            if (!action) return;

            switch (action.type) {
                case 'balloons/new':
                    if (!creatorModal) {
                        console.warn('[DeepLinkHandler] CreatorModalProvider not available — ignoring deep link');
                        return;
                    }
                    requiresUser({
                        callback: () => creatorModal.openCreatorNew(),
                    });
                    break;
            }
        },
        [creatorModal, requiresUser],
    );

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const anchor = (e.target as Element).closest('a');
            if (!anchor) return;
            const href = anchor.getAttribute('href') ?? '';
            if (!href.startsWith(SCHEME)) return;
            e.preventDefault();
            handleUrl(href);
        };

        const handleWindowEvent = (e: Event) => {
            const url = (e as CustomEvent<string>).detail;
            if (typeof url === 'string') handleUrl(url);
        };

        document.addEventListener('click', handleClick);
        window.addEventListener('balloonevents:deeplink', handleWindowEvent);

        return () => {
            document.removeEventListener('click', handleClick);
            window.removeEventListener('balloonevents:deeplink', handleWindowEvent);
        };
    }, [handleUrl]);
}
